import { useState } from "react"
import { useDropzone } from "react-dropzone"

import { Dropzone } from "./components/Dropzone"
import { ACCEPT } from "./config"
import { useCursorData } from "./contexts/CursorDataContext"
import { AnimatedWorkbench } from "./screens/AnimatedWorkbench"
import { StaticWorkbench } from "./screens/StaticWorkbench"

import type { PreviewBackground } from "./types/PreviewBackground"

export const CursorConverter = () => {
  const { source, isLoading, loadError, isConverting, handleNewSource } = useCursorData()
  const [previewBackground, setPreviewBackground] = useState<PreviewBackground>("transparent")

  // A file dropped anywhere on the workbench replaces the source instead of navigating to it.
  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    multiple: true,
    noClick: true,
    noKeyboard: true,
    noPaste: true,
    // A new source mid-conversion would remount the workbench and drop the file on its way back.
    noDrag: isConverting,
    onDrop: (accepted, rejected) =>
      void handleNewSource([...accepted, ...rejected.map(({ file }) => file)]),
  })

  if (!source) {
    return (
      <Dropzone
        error={loadError}
        isLoading={isLoading}
        onFiles={(files) => void handleNewSource(files)}
      />
    )
  }

  return (
    // Dimmed and locked while the next source decodes; this one stays on screen until it lands.
    <div
      {...getRootProps({
        inert: isLoading,
        "aria-busy": isLoading,
        className: `rounded-box transition-shadow ${
          isDragActive ? "ring-4 ring-primary/30" : ""
        } ${isLoading ? "opacity-60" : ""}`,
      })}
    >
      {/* Out of flow, so the zero-size input takes no room. */}
      <input
        {...getInputProps({
          accept: ACCEPT,
          "aria-label": "Choose the next image",
          className: "absolute",
        })}
      />
      {/* Keyed by the source, so a new drop starts the workbench over. */}
      {source.frames.length > 1 ? (
        <AnimatedWorkbench
          key={source.id}
          source={source}
          previewBackground={previewBackground}
          onPreviewBackgroundChange={setPreviewBackground}
          onNextFile={open}
        />
      ) : (
        <StaticWorkbench
          key={source.id}
          source={source}
          previewBackground={previewBackground}
          onPreviewBackgroundChange={setPreviewBackground}
          onNextFile={open}
        />
      )}
    </div>
  )
}
